import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, ShoppingCart } from 'lucide-react';
import { api } from '../../api/client.ts';
import { spinIconOut } from '../../utils/iconAnimations.ts';
import type { SearchResultData } from './searchTypes.ts';
import { typeColor, TYPE_LABELS } from './searchTypes.ts';
import { typeIcon } from './searchIcons.tsx';
import { navigateToResult } from './searchNav.ts';
import { AddToOrderModal } from '../../pages/Inventory.tsx';
import type { InventoryItem } from '../../types/index.ts';

const LIGHT_FRAME_TYPES = new Set(['catalogue', 'order']);

const OPEN_LABELS: Record<string, string> = {
  catalogue: 'Open item',
  vendor: 'Open vendor',
  order: 'Open order',
};

export default function SearchPreview({ result, onNavigated }: { result: SearchResultData; onNavigated: () => void }) {
  const navigate = useNavigate();
  const [photoIndex, setPhotoIndex] = useState(0);
  const [addToOrderItem, setAddToOrderItem] = useState<InventoryItem | null>(null);
  const [fetchingItem, setFetchingItem] = useState(false);

  const Icon = typeIcon(result.type);
  const light = LIGHT_FRAME_TYPES.has(result.type);
  const photos = result.previewImageUrls ?? [];
  const activePhoto = photos[Math.min(photoIndex, photos.length - 1)] ?? null;

  function open() {
    navigateToResult(navigate, result);
    onNavigated();
  }

  // Reuses the existing /inventory/items/:id read + AddToOrderModal -- no new server route, no
  // new modal. The search payload only carries the catalog preview fields, not the full
  // InventoryItem shape AddToOrderModal needs, so this fetches the real record first.
  async function handleAddToOrder() {
    if (result._sourcingStatus === 'placeholder') { open(); return; } // AddToOrderModal itself redirects placeholders -- let it, via the item page
    setFetchingItem(true);
    try {
      const res = await api.get<InventoryItem>(`/inventory/items/${result.id}`);
      const item = (res as unknown as { data: InventoryItem }).data;
      if (item) setAddToOrderItem(item);
    } catch { /* silent -- button just stays clickable, user can retry */ }
    finally { setFetchingItem(false); }
  }

  return (
    <div>
      <div className={`gs-preview-image-wrap ${light ? 'gs-thumb-light' : 'gs-thumb-dark'} ${light ? 'gs-thumb-contain' : 'gs-thumb-cover'}`}>
        {activePhoto ? <img src={activePhoto} alt="" /> : <div className="gs-empty-icon-frame"><Icon size={48} /></div>}
      </div>
      {photos.length > 1 && (
        <div className="gs-preview-dots">
          {photos.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`gs-preview-dot${i === photoIndex ? ' active' : ''}`}
              aria-label={`Photo ${i + 1}`}
              onClick={() => setPhotoIndex(i)}
            />
          ))}
        </div>
      )}

      <span className="gs-type-pill" style={{ background: `${typeColor(result.type)}26`, color: typeColor(result.type) }}>
        {TYPE_LABELS[result.type] ?? result.type}
      </span>
      <div className="gs-preview-title">{result.title}</div>
      {result.subtitle && <div className="gs-preview-sub">{result.subtitle}</div>}

      {result.previewFields.length > 0 && (
        <div className="gs-preview-fields">
          {result.previewFields.map((f, i) => (
            <div key={i}>
              <div className="gs-field-label">{f.label}</div>
              <div className={`gs-field-value${f.mono ? ' mono' : ''}`}>{f.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="gs-preview-actions">
        {result.type === 'catalogue' && (
          <button type="button" className="btn btn-primary combo1" onMouseLeave={spinIconOut} disabled={fetchingItem} onClick={handleAddToOrder}>
            <ShoppingCart size={14} className="btn-icon" /> {fetchingItem ? 'Loading…' : 'Add to order'}
          </button>
        )}
        <button type="button" className="btn btn-ghost combo1" onMouseLeave={spinIconOut} onClick={open}>
          <ExternalLink size={14} className="btn-icon" /> {OPEN_LABELS[result.type] ?? 'Open'}
        </button>
      </div>

      {addToOrderItem && (
        <AddToOrderModal item={addToOrderItem} onClose={() => setAddToOrderItem(null)} />
      )}
    </div>
  );
}
